import { motion, Variants } from 'framer-motion'
import React, { useMemo } from 'react'
import styled from 'styled-components'

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

interface CircularProgressProps {
  isLoading: boolean
  progress?: number
  size?: number
  strokeWidth?: number
  trackColor?: string
  progressColor?: string
  className?: string
  'aria-label'?: string
  children?: React.ReactNode
}

export const CircularProgress: React.FC<CircularProgressProps> = ({
  isLoading,
  progress = 0,
  size = 64,
  strokeWidth = 6,
  trackColor,
  progressColor,
  className,
  children,
  'aria-label': ariaLabel
}) => {
  const progressRatio = clamp(progress, 0, 100) / 100
  const radius = useMemo(() => (size - strokeWidth) / 2, [size, strokeWidth])
  const center = size / 2
  const viewBox = `0 0 ${size} ${size}`

  const circleVariants: Variants = useMemo(
    () => ({
      loading: {
        pathLength: 0.3,
        opacity: 1,
        pathOffset: 0
      },
      ready: {
        pathLength: progressRatio,
        pathOffset: 0,
        opacity: progressRatio > 0 ? 1 : 0,
        transition: {
          duration: 0.9,
          ease: 'easeInOut'
        }
      }
    }),
    [progressRatio]
  )

  const spinnerVariants: Variants = useMemo(
    () => ({
      loading: {
        rotate: 360,
        transition: {
          duration: 1.4,
          ease: 'linear',
          repeat: Infinity,
          repeatType: 'loop'
        }
      },
      ready: {
        rotate: 0,
        transition: {
          duration: 0.4,
          ease: 'easeOut'
        }
      }
    }),
    []
  )

  return (
    <Container
      $size={size}
      className={className}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={isLoading ? undefined : Math.round(progressRatio * 100)}
      aria-busy={isLoading}
      aria-label={ariaLabel}
    >
      <StyledSvg viewBox={viewBox} xmlns="http://www.w3.org/2000/svg">
        <TrackCircle
          cx={center}
          cy={center}
          r={radius}
          strokeWidth={strokeWidth}
          $trackColor={trackColor}
        />

        <motion.g
          variants={spinnerVariants}
          animate={isLoading ? 'loading' : 'ready'}
          initial={false}
        >
          <AnimatedCircle
            cx={center}
            cy={center}
            r={radius}
            strokeWidth={strokeWidth}
            $strokeColor={progressColor}
            variants={circleVariants}
            animate={isLoading ? 'loading' : 'ready'}
            initial={false}
          />
        </motion.g>
      </StyledSvg>

      {children ? <CenterContent>{children}</CenterContent> : null}
    </Container>
  )
}

const Container = styled.div<{ $size: number }>`
  position: relative;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
`

const CenterContent = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  pointer-events: none;
`

const StyledSvg = styled.svg`
  display: block;
  width: 100%;
  height: 100%;
  background-color: transparent;
`

const TrackCircle = styled.circle<{ $trackColor?: string }>`
  fill: transparent;
  stroke: ${({ $trackColor, theme }) => $trackColor || theme.colors.neutralGray};
  opacity: 0.4;
`

const AnimatedCircle = styled(motion.circle)<{ $strokeColor?: string }>`
  fill: transparent;
  stroke: ${({ $strokeColor, theme }) => $strokeColor || theme.colors.primary};
  stroke-linecap: round;
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
`
